/**
 * A Code 128 encoder (ISO/IEC 15417), hand-written so the label's bars are
 * exact module widths we control rather than a library's raster. Pure.
 *
 * A Code 128 symbol is: quiet zone, a start character, the data characters, a
 * check character, the stop pattern, quiet zone. Every character is 11 modules
 * wide — three bars and three spaces, each 1 to 4 modules — except the stop
 * pattern, which is 13 (it carries a closing 2-module bar). A character is one
 * of 107 patterns, numbered by value 0–105 plus the stop.
 *
 * **Subsets.** The same 107 patterns mean different things in three code sets.
 * Set B covers every printable ASCII character (space to DEL), one per symbol
 * (DEL itself is refused here; see `isCode128Encodable`).
 * Set C packs two digits into one symbol. Set A (control characters and
 * upper case only) is never needed here: an FNSKU or an ASIN is letters and
 * digits. So the encoder uses B and C, switching between them (values 100 and
 * 99) wherever that makes the symbol shorter; `chooseSets` finds the shortest
 * encoding by dynamic programming rather than the standard's run-length rules
 * of thumb, so it is optimal by construction.
 *
 * **Check character.** (start value + Σ position × value) mod 103, positions
 * counting the data characters (code-set switches included) from 1.
 *
 * **Quiet zone.** At least 10 modules on each side, by the standard. The
 * encoder does not draw it: the label layout reserves at least 0.25 in on each
 * side, which is more (see layout.ts).
 */

/**
 * The 107 patterns as six module widths (bar, space, bar, space, bar, space),
 * indexed by value; the stop pattern (index 106) has a seventh, closing bar.
 */
// prettier-ignore
export const CODE128_PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212',
  '221213', '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221',
  '223211', '221132', '221231', '213212', '223112', '312131', '311222', '321122', '321221',
  '312212', '322112', '322211', '212123', '212321', '232121', '111323', '131123', '131321',
  '112313', '132113', '132311', '211313', '231113', '231311', '112133', '112331', '132131',
  '113123', '113321', '133121', '313121', '211331', '231131', '213113', '213311', '213131',
  '311123', '311321', '331121', '312113', '312311', '332111', '314111', '221411', '431111',
  '111224', '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111', '111242',
  '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311',
  '113141', '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

/** Switch to code set C (from B). */
export const CODE_C = 99;
/** Switch to code set B (from C). */
export const CODE_B = 100;
export const START_B = 104;
export const START_C = 105;
export const STOP = 106;

/** Every character but the stop pattern is this many modules wide. */
export const MODULES_PER_CHARACTER = 11;
/** The stop pattern's width in modules. */
export const STOP_MODULES = 13;
/** The standard's minimum quiet zone on each side, in modules. */
export const QUIET_ZONE_MODULES = 10;

type CodeSet = 'B' | 'C';

/**
 * Whether a string can go on a label: non-empty, visible ASCII only (space to
 * tilde). DEL (0x7F) has a set B pattern (value 95) but is refused with the
 * other control characters: the PDF's built-in font cannot draw it, so the
 * line of text under the bars could not show what the bars carry.
 */
export function isCode128Encodable(text: string): boolean {
  return /^[\x20-\x7e]+$/.test(text);
}

function isDigit(text: string, index: number): boolean {
  const code = text.charCodeAt(index);
  return code >= 48 && code <= 57;
}

/**
 * The code set to be in at each character of the text for the shortest symbol:
 * one entry per character, a pair of digits encoded in C taking two entries.
 * Ties prefer staying in the current set, and B at the start.
 */
export function chooseSets(text: string): CodeSet[] {
  const n = text.length;
  // cost[set][i]: the fewest symbols that encode text[i..] when already in `set`.
  const costB = new Array<number>(n + 1).fill(0);
  const costC = new Array<number>(n + 1).fill(0);
  const pair = (i: number) => i + 1 < n && isDigit(text, i) && isDigit(text, i + 1);
  for (let i = n - 1; i >= 0; i -= 1) {
    const stayC = pair(i) ? 1 + (costC[i + 2] as number) : Infinity;
    const stayB = 1 + (costB[i + 1] as number);
    costB[i] = Math.min(stayB, 1 + stayC);
    costC[i] = Math.min(stayC, 1 + stayB);
  }

  const sets: CodeSet[] = [];
  let set: CodeSet = (costC[0] as number) < (costB[0] as number) ? 'C' : 'B';
  let i = 0;
  while (i < n) {
    const stayC = pair(i) ? 1 + (costC[i + 2] as number) : Infinity;
    const stayB = 1 + (costB[i + 1] as number);
    if (set === 'B' && 1 + stayC < stayB) set = 'C';
    else if (set === 'C' && 1 + stayB < stayC) set = 'B';
    if (set === 'C') {
      sets.push('C', 'C');
      i += 2;
    } else {
      sets.push('B');
      i += 1;
    }
  }
  return sets;
}

/**
 * The symbol's character values: the start character, the data (with any
 * code-set switches), and the check character. The stop pattern is not a value
 * in the checksum and is added by `code128Modules`.
 */
export function code128Values(text: string): number[] {
  if (!isCode128Encodable(text)) {
    throw new Error('Code 128 (sets B and C) encodes printable ASCII only.');
  }
  const sets = chooseSets(text);
  let set = sets[0] as CodeSet;
  const values: number[] = [set === 'C' ? START_C : START_B];
  let i = 0;
  while (i < text.length) {
    const wanted = sets[i] as CodeSet;
    if (wanted !== set) {
      values.push(wanted === 'C' ? CODE_C : CODE_B);
      set = wanted;
    }
    if (set === 'C') {
      values.push(Number(text.slice(i, i + 2)));
      i += 2;
    } else {
      values.push(text.charCodeAt(i) - 32);
      i += 1;
    }
  }
  values.push(code128Checksum(values));
  return values;
}

/** The check character for a start character followed by data values. */
export function code128Checksum(values: readonly number[]): number {
  let sum = values[0] ?? 0;
  for (let position = 1; position < values.length; position += 1) {
    sum += position * (values[position] as number);
  }
  return sum % 103;
}

/** One bar of the symbol: where it starts and how wide it is, in modules. */
export interface Bar {
  x: number;
  width: number;
}

export interface Code128Symbol {
  /** The start, data, and check values (no stop). */
  values: number[];
  /** The bars, left to right, in modules from the symbol's left edge. */
  bars: Bar[];
  /** The symbol's total width in modules, quiet zones excluded. */
  modules: number;
}

/** Encodes a text as a Code 128 symbol: its values and its bars in modules. */
export function encodeCode128(text: string): Code128Symbol {
  const values = code128Values(text);
  const bars: Bar[] = [];
  let x = 0;
  for (const value of [...values, STOP]) {
    const pattern = CODE128_PATTERNS[value] as string;
    for (let i = 0; i < pattern.length; i += 1) {
      const width = Number(pattern[i]);
      // Even positions are bars, odd positions are spaces.
      if (i % 2 === 0) bars.push({ x, width });
      x += width;
    }
  }
  return { values, bars, modules: x };
}
